import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { Tab, Tabs, Tooltip, useTheme, Button } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  ApiAdvancedFilter,
  getSelectedTab,
  PaginatedResponse,
  updateState,
  FilterableField
} from '../../../store/GeneralStore';
import { Correction, fetchCorrections, getTallyCorrectionsSubject } from '../../../store/Correction';
import { DefaultCorrectionGridColumnDefs } from './CorrectionGridColumnDefs';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
}

export const CorrectionPage = () => {
  const [correctionsLoading, setCorrectionsLoading] = useState<boolean>(false);
  const [corrections, setCorrections] = useState<PaginatedResponse<Correction>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().corrections ?? 0);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);
  const theme = useTheme();
  const filterKeys = DefaultCorrectionGridColumnDefs()
    .filter((column) => column.filterable)
    .map((column) => column.field);

  const defaultTabs = useMemo(
    () =>
      [
        {
          name: 'Active Run',
          filter: [
            {
              operator: 'is',
              field: 'paidInRunActive',
              value: 'true'
            }
          ],
          tooltip: 'Corrections being paid in the current active run.',
          disabledFilters: ['paidInRunDate']
        },
        {
          name: 'All',
          filter: [],
          tooltip: 'Unfiltered',
          disabledFilters: []
        }
      ] as TabProps[],
    []
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'correctionGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...customViewsTabProps];
  }, [defaultTabs, customViews]);

  const handleFetchCorrections = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setCorrectionsLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchCorrections(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setCorrectionsLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom') => {
      if (type === 'main') {
        setSelectedCustomView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else {
          return tab.name === name && !tab.customView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            corrections: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `correctionGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `correctionGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchCorrections(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [selectedTab, userCustomizations, allAvailableViews, getInitialSort, getInitialFilter, filterKeys]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const correctionsSub = getTallyCorrectionsSubject().subscribe((a) => setCorrections(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));

    return () => {
      if (correctionsSub) correctionsSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'correctionGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
        } else {
          setSelectedCustomView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, allAvailableViews]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      const allTabIndex = allAvailableViews.findIndex((tab) => tab.name === 'All' && !tab.customView);
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
  }, [selectedCustomView, customViewNames, allAvailableViews, handleTabChange]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  return (
    <PageLayout
      dataCy="correction-page"
      dataTestId="correction-page"
      pageTitle="Corrections"
      className="w-full mt-0">
      <Fragment>
        <Tabs
          sx={DEFAULT_TAB_SX_STYLES}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, index) => {
            const currentTab = allAvailableViews[index];
            if (currentTab) {
              if (currentTab.customView) {
                handleTabChange(index, currentTab.name, 'custom');
              } else {
                handleTabChange(index, currentTab.name, 'main');
              }
            }
          }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height: allAvailableViews[selectedTab]?.customView ? 0 : 2,
                borderRadius: 1
              }
            }
          }}>
          {defaultTabs.map((tab, _index) => (
            <Tab
              sx={{ textTransform: 'none', fontSize: '.8rem', color: 'grey6' }}
              key={tab.name}
              label={
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        width: 'auto'
                      }
                    }
                  }}>
                  <span>{tab.name}</span>
                </Tooltip>
              }
            />
          ))}
        </Tabs>
        <div className="-mt-12 float-right inline-flex pr-4">
          <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="custom-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedCustomView}
                selectedView={selectedCustomView}
                viewOptions={customViewNames}
                handleViewChange={handleCustomViewMenuChange}
                defaultTitle="My Views"
              />
            </div>
          </div>
          <div className="ml-3 self-center">
            <Button
              onClick={() => setTriggerSave(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Save As View
            </Button>
          </div>
          <div
            className="ml-3 self-center"
            key="add-custom-view">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<EditNoteIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Edit Views
            </Button>
          </div>
        </div>
        {allAvailableViews.map((tab, index) => {
          let tabKeyName = tab.name || '';
          if (!tab.customView) {
            tabKeyName = tabKeyName.replace(/\s/g, '');
          }
          const tabKey = `correctionGrid_${tabKeyName}` as PrefKey;
          const colsForTab = getColsForTab(DefaultCorrectionGridColumnDefs(), tab.disabledFilters ?? []);
          const gridSettingsForTab = userCustomizations?.content?.[tabKey];
          let initialState;
          if (gridSettingsForTab) {
            initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
            const initialFilterModel = getInitialFilter(userCustomizations);
            if (initialState && initialFilterModel) {
              if (initialState.filter) {
                initialState.filter.filterModel = initialFilterModel;
              } else {
                initialState.filter = { filterModel: initialFilterModel };
              }
            }
          }

          return (
            <Fragment key={tab.name}>
              {(!initialState || correctionsLoading) && (
                <div className="flex h-[45rem] m-auto">
                  <ComponentSpinner label="Loading data..." />
                </div>
              )}
              {initialState && !correctionsLoading && userCustomizations && (
                <TabPanel
                  key={tab.name}
                  index={index}
                  value={selectedTab}
                  className="max-w-[60vw] min-w-full">
                  <UpstackDataGrid
                    title="Corrections"
                    rows={corrections?.data || []}
                    columns={colsForTab}
                    initialState={initialState}
                    page={tabKey}
                    gridSettings={gridSettingsForTab}
                    showSearch={false}
                    apiFilter={true}
                    fetchFunction={handleFetchCorrections}
                    rowCount={corrections?.totalCount}
                    advancedFiltering={true}
                    filterKeys={filterKeys}
                    triggerSave={triggerSave}
                    handleSaveComplete={() => setTriggerSave(false)}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                    customNoRowsMessage="No corrections available"
                  />
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </Fragment>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="correction"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || 'All'}
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
        type="correction"
      />
    </PageLayout>
  );
};
